"use client";

import { useEffect, useRef } from "react";
import Section, { SectionHeader } from "@/components/Section";
import { IconCheck } from "@/components/icons";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

const POINTS = [
  "Signed with a per-webhook secret you control",
  "Up to 5 automatic retries with 30s backoff",
  "Every attempt logged: time, status, HTTP code, duration",
  "One-click manual replay from the dashboard",
  "Multiple webhooks per campaign — fan out to billing, CRM, ops",
  "Background processor decouples delivery from request latency",
];

const ROTATE_INTERVAL_MS = 2800;
const ITEM_GAP = 14;

function computeSlot(itemIndex: number, centerIndex: number, length: number) {
  let diff = ((itemIndex - centerIndex) % length + length) % length;
  if (diff > length / 2) diff -= length;
  return diff;
}

export default function WebhookSection() {
  const containerRef = useRef<HTMLDivElement>(null);
  const stageRef = useRef<HTMLDivElement>(null);
  const highlightRef = useRef<HTMLDivElement>(null);
  const itemRefs = useRef<(HTMLDivElement | null)[]>([]);
  const centerIndexRef = useRef(0);

  useEffect(() => {
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    gsap.registerPlugin(ScrollTrigger);
    if (!containerRef.current) return;

    const ctx = gsap.context(() => {
      const trigger = {
        trigger: containerRef.current,
        start: "top 75%",
        once: true,
      } as const;

      gsap.from(".anim-webhook-list", {
        scrollTrigger: trigger,
        x: -50,
        duration: 0.8,
        ease: "power3.out",
      });

      gsap.from(".anim-webhook-text", {
        scrollTrigger: trigger,
        x: 50,
        duration: 0.8,
        delay: 0.1,
        ease: "power3.out",
      });
    }, containerRef);

    return () => ctx.revert();
  }, []);

  useEffect(() => {
    if (!stageRef.current) return;
    const length = POINTS.length;

    const measureStep = () => {
      const first = itemRefs.current[0];
      const itemHeight = first?.getBoundingClientRect().height ?? 72;
      return itemHeight + ITEM_GAP;
    };

    const syncHighlightHeight = () => {
      const first = itemRefs.current[0];
      const itemHeight = first?.getBoundingClientRect().height ?? 76;
      if (highlightRef.current) {
        highlightRef.current.style.height = `${itemHeight}px`;
      }
    };

    let step = measureStep();
    if (stageRef.current) {
      stageRef.current.style.height = `${3 * step - ITEM_GAP + 8}px`;
    }
    syncHighlightHeight();

    const applySlot = (el: HTMLElement, slot: number, animate: boolean) => {
      const isCenter = slot === 0;
      const isAdjacent = Math.abs(slot) === 1;
      const opacity = isCenter ? 1 : isAdjacent ? 1 : 0;
      const scaleX = isCenter ? 1 : 0.92;
      const scaleY = isCenter ? 1 : 0.98;
      const filter = isCenter ? "blur(0px)" : isAdjacent ? "blur(0px)" : "blur(3px)";
      el.dataset.slot = isCenter
        ? "center"
        : slot === -1
          ? "edge-top"
          : slot === 1
            ? "edge-bottom"
            : "hidden";
      const props = {
        yPercent: -50,
        y: slot * step,
        opacity,
        scaleX,
        scaleY,
        filter,
      };
      if (animate) {
        gsap.to(el, { ...props, duration: 0.7, ease: "power3.out", overwrite: "auto" });
      } else {
        gsap.set(el, props);
      }
    };

    itemRefs.current.forEach((el, i) => {
      if (!el) return;
      applySlot(el, computeSlot(i, centerIndexRef.current, length), false);
    });

    let intervalId = 0;
    let isVisible = false;
    let isPageVisible = typeof document !== "undefined" ? !document.hidden : true;

    const tick = () => {
      const prevCenter = centerIndexRef.current;
      const nextCenter = (prevCenter + 1) % length;
      centerIndexRef.current = nextCenter;

      itemRefs.current.forEach((el, i) => {
        if (!el) return;
        const prevSlot = computeSlot(i, prevCenter, length);
        const newSlot = computeSlot(i, nextCenter, length);
        const isWraparound = newSlot - prevSlot !== -1;
        applySlot(el, newSlot, !isWraparound);
      });
    };

    const start = () => {
      if (intervalId !== 0 || !isVisible || !isPageVisible) return;
      intervalId = window.setInterval(tick, ROTATE_INTERVAL_MS);
    };

    const stop = () => {
      if (intervalId === 0) return;
      window.clearInterval(intervalId);
      intervalId = 0;
    };

    const handleResize = () => {
      step = measureStep();
      if (stageRef.current) {
        stageRef.current.style.height = `${3 * step - ITEM_GAP + 8}px`;
      }
      syncHighlightHeight();
      itemRefs.current.forEach((el, i) => {
        if (!el) return;
        applySlot(el, computeSlot(i, centerIndexRef.current, length), false);
      });
    };

    const io =
      typeof IntersectionObserver !== "undefined"
        ? new IntersectionObserver(
            (entries) => {
              for (const entry of entries) {
                isVisible = entry.isIntersecting;
              }
              if (isVisible) start();
              else stop();
            },
            { rootMargin: "100px" }
          )
        : null;

    if (io && stageRef.current) io.observe(stageRef.current);

    const handleVisibility = () => {
      isPageVisible = !document.hidden;
      if (isPageVisible) start();
      else stop();
    };

    window.addEventListener("resize", handleResize);
    if (typeof document !== "undefined") {
      document.addEventListener("visibilitychange", handleVisibility);
    }

    return () => {
      stop();
      if (io) io.disconnect();
      window.removeEventListener("resize", handleResize);
      if (typeof document !== "undefined") {
        document.removeEventListener("visibilitychange", handleVisibility);
      }
    };
  }, []);

  return (
    <Section id="webhooks" className="overflow-clip bg-gradient-to-b from-black via-zinc-950/30 to-black">
      <div ref={containerRef} className="grid items-center gap-12 lg:grid-cols-[1.3fr_1fr]">

        {/* Left Side: Rotating Feature List */}
        <div className="anim-webhook-list relative w-full max-w-2xl mx-auto lg:mx-0 lg:order-1 order-2">
          <div className="absolute -inset-4 rounded-[2rem] bg-gradient-to-br from-copper-500/15 via-transparent to-transparent opacity-60 blur-2xl transform-gpu will-change-[filter,transform]" />

          <div
            ref={stageRef}
            className="relative w-full overflow-hidden"
            style={{ height: "280px" }}
          >
            {POINTS.map((p, i) => (
              <div
                key={p}
                ref={(el) => {
                  itemRefs.current[i] = el;
                }}
                className="webhook-item absolute left-0 right-0 top-1/2 flex min-h-[76px] items-center gap-3 rounded-xl border border-copper-500/30 bg-white/[0.03] p-4 backdrop-blur-sm transform-gpu will-change-[transform,opacity,filter]"
              >
                <span className="inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full border border-copper-700/40 bg-copper-900/30 text-copper-300">
                  <IconCheck className="h-3 w-3" />
                </span>
                <span className="text-sm text-zinc-300">{p}</span>
              </div>
            ))}

            {/* Static highlight — stays at center; items rotate through it */}
            <div
              ref={highlightRef}
              aria-hidden
              className="webhook-highlight pointer-events-none absolute left-0 right-0 top-1/2 z-20 -translate-y-1/2 overflow-hidden rounded-xl"
              style={{ minHeight: "76px" }}
            >
              <div className="webhook-highlight__ring absolute inset-0 rounded-xl" />
              <div className="webhook-highlight__bg absolute inset-0 rounded-xl" />
              <div className="webhook-highlight__sheen absolute inset-0 rounded-xl" />
              <div className="webhook-highlight__bar absolute left-0 top-1/2 h-9 w-[3px] -translate-y-1/2 rounded-r-full" />
            </div>
          </div>
        </div>

        {/* Right Side: Text */}
        <div className="anim-webhook-text flex flex-col items-start lg:order-2 order-1 text-left">
          <SectionHeader
            eyebrow="Reward Automation"
            title={
              <>
                Automate rewards instantly{" "}
                <span className="text-gradient-warm">without manual work</span>
              </>
            }
            description="When a referral converts, GrowthRail automatically notifies your system to issue the reward. With built-in retries and delivery logs, you never have to manually credit a user again."
            align="left"
            className="!mx-0"
          />
        </div>

      </div>
    </Section>
  );
}
